/**
 * Article rédigé le 2026-09-06 à partir de sources web ouvertes et vérifiées automatiquement
 * (6 URL testées, révisé après vérification). Daté au moment des faits : 2025-10-16.
 * À relire avant/après mise en ligne. Format : voir src/blog/README.md
 */
export default {
  "slug": "cybersecday-2025-besancon-cci-ia-cyber",
  "title": "CyberSecDay 2025 : à Besançon, la CCI ouvre et referme sa journée cyber sur l’IA",
  "excerpt": "Le 14 octobre, la Maison de l’économie accueillait la deuxième édition du CyberSecDay de la CCI Saône-Doubs. Un programme gratuit qui s’ouvrait et se refermait sur l’intelligence artificielle.",
  "seoTitle": "CyberSecDay 2025 : la CCI place l’IA en tête",
  "seoDescription": "Le 14 octobre 2025, la CCI Saône-Doubs réunissait à la Maison de l’économie la 2e édition du CyberSecDay : une quinzaine de séquences, de l’IA au risque cyber.",
  "category": "evenements",
  "tags": [
    "CyberSecDay",
    "CCI Saône-Doubs",
    "CSIRT-BFC",
    "Cybersécurité",
    "Maison de l'économie"
  ],
  "author": "jimmy",
  "publishedAt": "2025-10-16",
  "updatedAt": "2025-10-16",
  "featured": false,
  "cover": {
    "image": "/img/blog/besancon-la-cci-et-le-lycee-jules-haag.jpg",
    "credit": "Photo : Besançon, la CCI et le lycée Jules Haag — Wikipedro, CC BY-SA 4.0, via Wikimedia Commons",
    "alt": "Vue en plongée sur le bâtiment de la Maison de l'économie, siège de la CCI Saône-Doubs à Besançon, reconnaissable à sa longue façade vitrée incurvée, avec le lycée Jules-Haag et les toits de la ville en arrière-plan."
  },
  "content": [
    {
      "type": "lead",
      "text": "C’est à la Maison de l’économie, 46 avenue Villarceau[^1], que la [CCI Saône-Doubs](/blog/open-business-cci-jerome-colombain-ia-entreprises-2024) avait programmé le mardi 14 octobre la deuxième édition de son **CyberSecDay**[^6]. De 9 h à 19 h 30, pauses comprises, le programme diffusé par la chambre consulaire enchaînait mini-conférences, ateliers et masterclasses autour d’une thématique placée en ouverture comme en clôture : l’intelligence artificielle, objet de la première mini-conférence de la matinée et de la conférence finale de la soirée[^2]."
    },
    {
      "type": "keyfacts",
      "facts": [
        {
          "label": "Quoi",
          "value": "CyberSecDay, journée gratuite consacrée au risque cyber en entreprise, deuxième édition"
        },
        {
          "label": "Quand",
          "value": "Mardi 14 octobre 2025, de 9 h à 19 h 30"
        },
        {
          "label": "Où",
          "value": "Maison de l’économie, 46 avenue Villarceau, Besançon"
        },
        {
          "label": "Qui",
          "value": "CCI Saône-Doubs, avec notamment la Région Bourgogne-Franche-Comté, la CPME 25, l’EDIH DEDIHCATED et 3S-Sécurité"
        },
        {
          "label": "Pour qui",
          "value": "Dirigeants, responsables IT, RH, juristes, experts-comptables et agents des collectivités"
        }
      ]
    },
    {
      "type": "h2",
      "text": "Des séquences courtes, du discours d’ouverture de 9 h à la clôture de 19 h 30"
    },
    {
      "type": "p",
      "text": "Le parti pris de la CCI Saône-Doubs tenait au rythme : plutôt qu’une poignée de plénières longues, une quinzaine de séquences de trente minutes à une heure et demie, réparties de 9 h à 19 h 30 et entrecoupées de deux pauses[^2]. L’accès était gratuit sur inscription, et le public visé volontairement large : chefs d’entreprise et cadres dirigeants, mais aussi responsables informatiques, directeurs de sûreté, experts-comptables, directeurs juridiques et agents des collectivités[^1]. Ce format court permet à un dirigeant de PME de venir deux heures sans bloquer sa journée — un détail d’organisation qui pèse lourd quand la cible est le tissu des TPE-PME du Doubs."
    },
    {
      "type": "p",
      "text": "Après un discours d’ouverture de 9 h à 9 h 30, le programme diffusé par la CCI distinguait quatre types de séquences[^2] :"
    },
    {
      "type": "ul",
      "items": [
        "Huit **mini-conférences** de trente minutes : intelligence artificielle et rythme de la sécurité, pilotage de la cybersécurité et dispositifs d’aide, stratégie de continuité d’activité, cybersécurité industrielle, darknets et Internet alternatif, approche humaine de la sécurité, politique de sécurité de l’information, cyberassurance ;",
        "Des **ateliers** en parallèle en milieu de matinée : « Cybersécurité obligatoire : ce que NIS2 change concrètement pour les entreprises » et « Le rôle des RH dans la cybersécurité : sensibilisation et clauses contractuelles » ;",
        "Deux **masterclasses** d’une heure : « Cyberattaque en cours : que dire, que faire, et dans quel ordre ? » le matin, puis « Détection, réponse, SOC : priorité stratégique ou simple option défensive ? » de 16 h à 17 h ;",
        "Une **conférence finale** annoncée de 18 h à 19 h 30, ouverte sur l’intelligence artificielle."
      ]
    },
    {
      "type": "h2",
      "text": "L’IA ouvre et referme le programme"
    },
    {
      "type": "p",
      "text": "Le sujet n’était pas relégué en fin de journée : dès 9 h 30, la première mini-conférence posait la question frontalement — « Intelligence artificielle : la sécurité peut-elle suivre le rythme ? »[^2]. Le programme refermait la journée sur la même thématique, avec **Clément Domingo**, alias *SaxX*, présenté par la CCI comme « Gentil Hacker et Cyber évangéliste »[^1] et cofondateur du collectif Hackers Sans Frontières[^6], pour une conférence annoncée de 18 h à 19 h 30 sous le titre « IA : état des lieux, perspectives et enjeux de cybersécurité »[^2]."
    },
    {
      "type": "p",
      "text": "L’angle est cohérent avec ce que la presse économique régionale relevait du programme : hameçonnage et *deepfakes*, intégration de l’intelligence artificielle dans les outils d’ingénierie sociale, mais aussi arbitrages d’investissement et gouvernance du risque[^6]. Autrement dit, la même technologie qui industrialise le courriel frauduleux crédible sert aussi à trier les alertes d’un SOC. Pour une PME bisontine de trente salariés, la question pratique n’est pas de choisir un camp mais de savoir ce qui, dans cette course, relève de son propre budget."
    },
    {
      "type": "h2",
      "text": "NIS2, RH, continuité : la cybersécurité sort de la salle serveurs"
    },
    {
      "type": "p",
      "text": "C’est sans doute le message le plus insistant de cette édition. Deux des créneaux de la matinée ne s’adressaient pas d’abord à des techniciens : la conformité **NIS2** relève de la direction générale et du juridique, le rôle des ressources humaines de la sensibilisation et des clauses contractuelles[^2]. L’après-midi prolongeait la logique avec la stratégie de continuité d’activité et la mise en place d’un PCA, puis la cyberassurance[^2]."
    },
    {
      "type": "p",
      "text": "Les ordres de grandeur cités autour de l’événement expliquent cette bascule : les attaques par rançongiciel auraient été multipliées par dix en trois ans, le coût médian d’une cyberattaque avoisinerait 50 000 euros, et 40 % des PME touchées subiraient au moins huit heures d’interruption d’activité[^6]. Huit heures d’arrêt, pour un sous-traitant industriel du bassin, ce n’est plus un incident informatique : c’est une ligne de production à l’arrêt et un client à prévenir."
    },
    {
      "type": "h2",
      "text": "Le CSIRT-BFC, le numéro à composer quand tout s’arrête"
    },
    {
      "type": "p",
      "text": "Le centre régional de cybersécurité de Bourgogne-Franche-Comté, le **CSIRT-BFC**, relayait l’événement auprès de ses bénéficiaires[^4]. Sa présence dans l’écosystème n’est pas anecdotique : service public gratuit né d’une volonté conjointe de l’ANSSI et de la Région, porté par l’agence régionale du numérique et de l’intelligence artificielle, il intervient sur quatre thématiques — remédiation, alerte, sensibilisation et animation — au bénéfice des collectivités, des organismes publics, des entreprises et des associations du territoire[^5]. Concrètement, il oriente l’organisation victime vers des prestataires spécialisés et l’accompagne durant toute la durée de l’incident, étant entendu que les coûts et la contractualisation avec ces prestataires restent à la charge de la structure touchée[^5]."
    },
    {
      "type": "callout",
      "tone": "tip",
      "title": "Pourquoi ça compte pour Besançon",
      "text": "Le bassin bisontin concentre des sous-traitants de précision, des acteurs de la santé et des collectivités de taille modeste. Autant de structures qui entrent dans le périmètre du CSIRT-BFC, ouvert aux collectivités, organismes publics, entreprises et associations[^5], et dans le public visé par la CCI[^1]. Pour ces structures, sans direction des systèmes d’information dédiée, une journée gratuite qui met dans la même salle NIS2, RH, assurance et détection technique vaut mieux qu’un audit qu’elles ne commanderont pas."
    },
    {
      "type": "h2",
      "text": "Ce qu’il faut retenir"
    },
    {
      "type": "p",
      "text": "Le CyberSecDay en est à sa deuxième édition[^6] : format resserré sur une journée, accès gratuit sur inscription[^1], et un adossement à des partenaires régionaux, parmi lesquels la Région Bourgogne-Franche-Comté, la CPME 25, l’[EDIH DEDIHCATED](/blog/pfia-2025-dijon-recherche-ia-francophone) et 3S-Sécurité[^3]. À l’heure où nous publions, la CCI Saône-Doubs n’a pas communiqué de bilan chiffré de fréquentation ni de compte rendu du déroulé ; nous compléterons cet article si ces éléments sont rendus publics. Reste l’essentiel : sur un territoire où la cybersécurité était longtemps perçue comme une affaire de spécialistes, cette édition aura surtout cherché à la remettre là où elle se décide, c’est-à-dire au comité de direction."
    }
  ],
  "references": [
    {
      "id": 1,
      "title": "CyberSecDay – Le rendez-vous des entreprises face au risque cyber",
      "publisher": "CCI Saône-Doubs",
      "url": "https://www.saone-doubs.cci.fr/cybersecday",
      "type": "official",
      "date": ""
    },
    {
      "id": 2,
      "title": "CyberSecDay 2025 – Le programme",
      "publisher": "CCI Saône-Doubs",
      "url": "https://www.saone-doubs.cci.fr/cybersecday/programme",
      "type": "official",
      "date": ""
    },
    {
      "id": 3,
      "title": "CyberSecDay 2025 – Partenaires de l’événement",
      "publisher": "CCI Saône-Doubs",
      "url": "https://www.saone-doubs.cci.fr/cybersecday/partenaires",
      "type": "official",
      "date": ""
    },
    {
      "id": 4,
      "title": "Cybersecday à Besançon le 14 octobre 2025",
      "publisher": "CSIRT-BFC – Centre régional de cybersécurité Bourgogne-Franche-Comté",
      "url": "https://www.csirt-bfc.fr/cybersecday-a-besancon-le-14-octobre-2025/",
      "type": "official",
      "date": ""
    },
    {
      "id": 5,
      "title": "Le CSIRT-BFC",
      "publisher": "CSIRT-BFC – Centre régional de cybersécurité Bourgogne-Franche-Comté",
      "url": "https://www.csirt-bfc.fr/le-csirt-bfc/",
      "type": "official",
      "date": ""
    },
    {
      "id": 6,
      "title": "CyberSec’Day, un rendez-vous phare de la cybersécurité à Besançon le 14 octobre",
      "publisher": "Traces Écrites News",
      "url": "https://www.tracesecritesnews.fr/actualite/cybersec-day-un-rendez-vous-phare-de-la-cybersecurite-a-besancon-le-14-octobre-226905",
      "type": "press",
      "date": "2025-09-25"
    }
  ]
};
